import {SourceShareBadge,aggregateSourceShares,type Ga4SourceShare} from './Ga4SourceShare';
export interface Ga4Observation {
  source_share?: Ga4SourceShare | null;
  sessions: number | null;
  revenue: number | null;
  orders: number | null;
  currency_code: string | null;
  quality: string;
  collected_at: string;
  time_zones: string[];
  attribution: { utm_source: string; utm_medium?: string | null; utm_campaign?: string | null }[];
}
export interface Ga4HistoryRow {
  clicks?: number | string | null;
  delivery_missing?: boolean;
  ga4?: Ga4Observation | null;
}
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
// PostgreSQL numeric/bigint values can arrive as strings; missing values must not become zero.
const numeric = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string' || typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  return finite(parsed) ? parsed : null;
};
const count = (value: number | null | undefined) => finite(value) ? value.toLocaleString() : '—';
const money = (value: number | null | undefined, currency?: string | null) => {
  if (!finite(value) || !currency || !/^[A-Z]{3}$/.test(currency)) return '—';
  return value.toLocaleString(undefined, { style: 'currency', currency });
};
const connectRate = (row: Ga4HistoryRow) => {
  const g = row.ga4;
  const clicks = numeric(row.clicks);
  if (!g || !finite(g.sessions) || g.sessions < 0 || row.delivery_missing || clicks == null || clicks <= 0) return null;
  const rate = g.sessions / clicks;
  return finite(rate) ? rate : null;
};
const percentage = (value: number | null) => value == null ? '—' : value.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rateNote = 'Estimated: GA4 sessions / clicks shown in this table. Daily calendars may differ. No 100% cap. Partial or stale observations show a provisional rate (*). Unavailable for missing sessions or delivery, or zero clicks.';
const cellClass = 'px-4 py-2.5 text-right text-slate-700 whitespace-nowrap';

export function Ga4HistoryHeaders() {
  return <>{['Sessions · GA4', 'Revenue · GA4', 'Orders · GA4', 'Connect rate'].map(label =>
    <th key={label} className="px-4 py-2.5 font-medium text-right whitespace-nowrap" title={label === 'Connect rate' ? rateNote : undefined}>{label}</th>
  )}</>;
}

export function Ga4HistoryCells({ row }: { row: Ga4HistoryRow }) {
  const g = row.ga4;
  const flag = g && g.quality !== 'complete' ? '*' : '';
  const rate = connectRate(row);
  const timestamp = g?.collected_at && Number.isFinite(Date.parse(g.collected_at)) ? new Date(g.collected_at).toLocaleString() : '—';
  const details = g ? `GA4: ${g.attribution.map(a => [a.utm_source, a.utm_medium, a.utm_campaign].filter(Boolean).join(' / ')).join('; ')}. Collected: ${timestamp}. Calendar: ${g.time_zones.join(', ')}. Status: ${g.quality}.` : 'No GA4 observation for this day.';
  return <>
    <td className={cellClass} title={details}>{count(g?.sessions)}{flag}</td>
    <td className={cellClass} title={details}>{money(g?.revenue, g?.currency_code)}{flag}<SourceShareBadge share={g?.source_share} metric="revenue" /></td>
    <td className={cellClass} title={details}>{count(g?.orders)}{flag}<SourceShareBadge share={g?.source_share} metric="orders" /></td>
    <td className={cellClass} title={`${rateNote} ${details}`}>{percentage(rate)}{rate != null ? flag : ''}</td>
  </>;
}

export function Ga4HistoryTotals({ rows }: { rows: Ga4HistoryRow[] }) {
  const observed = rows.flatMap(row => row.ga4 ? [row.ga4] : []);
  const share = aggregateSourceShares(rows.map(row => row.ga4?.source_share));
  const full = observed.length === rows.length && observed.every(g => g.quality === 'complete');
  const total = (key: 'sessions' | 'revenue' | 'orders') => observed.length && observed.every(g => finite(g[key])) ? observed.reduce((sum, g) => sum + g[key]!, 0) : null;
  const currencies = [...new Set(observed.map(g => g.currency_code))];
  const currency = currencies.length === 1 ? currencies[0] : null;
  const sessions = total('sessions');
  const rates = rows.flatMap(row => {
    const value = connectRate(row);
    return value == null ? [] : [{ value, provisional: row.ga4?.quality !== 'complete' }];
  });
  const rate = rates.length ? rates.reduce((sum, entry) => sum + entry.value, 0) / rates.length : null;
  const rateFlag = rates.length < rows.length || rates.some(entry => entry.provisional) ? '*' : '';
  const flag = full ? '' : '*';
  const details = `Available GA4 observations: ${observed.length}/${rows.length} days. ${full ? '' : 'Partial total: missing or incomplete days.'} Revenue is not combined across currencies.`;
  return <>
    <td className={cellClass} title={details}>{count(sessions)}{flag}</td>
    <td className={cellClass} title={details}>{money(total('revenue'), currency)}{flag}<SourceShareBadge share={share} metric="revenue" /></td>
    <td className={cellClass} title={details}>{count(total('orders'))}{flag}<SourceShareBadge share={share} metric="orders" /></td>
    <td className={cellClass} title={`Arithmetic average of available daily connect rates: ${rates.length}/${rows.length} days. ${rateNote}`}>{percentage(rate)}{rate != null ? rateFlag : ''}</td>
  </>;
}

export function Ga4HistoryNote() {
  return <p className="px-4 py-3 text-xs text-slate-500 border-t border-border">
    Share beside Revenue and Orders = configured utm_source / entire GA4 property, across all media and campaigns. Period shares divide summed values, not daily percentages.
    GA4: * partial or stale observation; totals include available days only. Revenue uses the GA4 currency.
    {' '}Connect rate = sessions / clicks shown here, estimated because daily calendars may differ.
    {' '}Delivery summary percentages are daily averages; * on connect rate means provisional data or excluded days with no valid rate.
    {' '}— means missing data or no clicks.
  </p>;
}
